import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";

import { Breadcrumbs } from "./components/Breadcrumbs.jsx";
import { ErrorAlert } from "./components/ErrorAlert.jsx";
import { OptionalFieldsBlock } from "./components/OptionalFieldsBlock.jsx";
import { useWithdrawalWizardContext } from "./useWithdrawalWizard.js";

const inputClass =
  "wp:block wp:w-full wp:appearance-none wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:[background-color:var(--wf-input-bg,#ffffff)] wp:px-4 wp:py-3 wp:text-lg wp:[color:var(--wf-text-color,#111827)] wp:shadow-sm wp:focus:border-gray-900 wp:focus:outline-none wp:focus:ring-1 wp:focus:ring-gray-900";

export function ManualStep({ optionalFields, designMode }) {
  const { t } = useTranslation();
  const { register, handleSubmit, setValue, control } = useFormContext();
  const { error, goToCustomer, loading, manualReasons, submitManual } = useWithdrawalWizardContext();
  const withdrawalReasonId = useWatch({ control, name: "withdrawal_reason_id" });
  const selectedReason = manualReasons.find((candidate) => String(candidate.id) === String(withdrawalReasonId));
  const showOther = selectedReason?.key === "other";

  return (
    <div className="wp:mx-auto wp:max-w-xl">
      <Breadcrumbs current={2} />
      <h1 className="wp:mb-6 wp:text-3xl wp:font-normal wp:tracking-tight wp:[color:var(--wf-text-color,#111827)] wp:md:text-4xl">
        {t("proxy.withdrawals.manual.title")}
      </h1>
      <ErrorAlert error={error} />
      <p className="wp:mb-8 wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:bg-gray-50 wp:p-4 wp:text-base wp:text-gray-700">
        {t("proxy.withdrawals.manual.intro")}
      </p>

      <form onSubmit={handleSubmit(submitManual)}>
        <div className="wp:mb-8">
          <label htmlFor="widerruf-items" className="wp:mb-2 wp:block wp:text-base wp:font-medium">
            {t("proxy.withdrawals.manual.items_description_label")}{" "}
            <span className="wp:text-red-600">*</span>
          </label>
          <span className="wp:mb-3 wp:block wp:text-sm wp:text-gray-500">
            {t("proxy.withdrawals.manual.items_description_help")}
          </span>
          <textarea
            id="widerruf-items"
            required
            rows={4}
            {...register("items_description", { required: true })}
            placeholder={t("proxy.withdrawals.manual.items_description_placeholder")}
            className={`${inputClass} wp:resize-y`}
          />
        </div>

        {manualReasons.length > 0 ? (
          <div className="wp:mb-8">
            <label htmlFor="widerruf-reason" className="wp:mb-2 wp:block wp:text-base wp:font-medium">
              {t("proxy.withdrawals.manual.reason_label")}
            </label>
            <select
              id="widerruf-reason"
              {...register("withdrawal_reason_id", {
                onChange: (event) => {
                  if (event.target.selectedOptions[0]?.dataset.reasonKey !== "other") {
                    setValue("reason", "");
                  }
                },
              })}
              className={inputClass}
            >
              <option value="">{t("proxy.withdrawals.common.select_placeholder")}</option>
              {manualReasons.map((candidate) => (
                <option key={candidate.id} value={candidate.id} data-reason-key={candidate.key}>
                  {t(`withdrawal_reasons.${candidate.key}`, { defaultValue: candidate.key })}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showOther ? (
          <div className="wp:mb-8">
            <label htmlFor="widerruf-other-reason" className="wp:mb-2 wp:block wp:text-base wp:font-medium">
              {t("proxy.withdrawals.common.other_reason_label")}
            </label>
            <input
              id="widerruf-other-reason"
              {...register("reason")}
              className={inputClass}
            />
          </div>
        ) : null}

        <OptionalFieldsBlock flags={optionalFields} />
        <p className="wp:mb-8 wp:rounded-md wp:bg-gray-50 wp:p-4 wp:text-sm wp:text-gray-600">
          {t("proxy.withdrawals.manual.declaration")}
        </p>
        <div className="wp:flex wp:flex-col wp:gap-4">
          <button
            type="submit"
            disabled={loading || designMode}
            className="wp:w-full wp:rounded-md wp:border wp:border-solid wp:[border-color:var(--wf-button-border,#111827)] wp:[background-color:var(--wf-button-bg,#111827)] wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:[color:var(--wf-button-text,#ffffff)] wp:shadow-sm wp:transition-opacity wp:enabled:cursor-pointer wp:hover:opacity-90 wp:disabled:cursor-not-allowed wp:disabled:opacity-50 wp:sm:flex-1"
          >
            {t("proxy.withdrawals.common.actions.submit")}
          </button>
          <button
            type="button"
            onClick={goToCustomer}
            className="wp:w-full wp:cursor-pointer wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:bg-white wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:text-gray-700 wp:shadow-sm"
          >
            {t("proxy.withdrawals.common.actions.back")}
          </button>
        </div>
      </form>
    </div>
  );
}
